// shell/panes/table-pane.ts
// Table-only pane factory

import type { PaneType, PaneInstance, PaneTypeRenderContext } from '../core/types';
import { createElement } from '../utils/dom';
import { createListPaneContainer } from '../utils/pane-ui';
import { DataListView } from '../components/data-list-view';
import type { DataListViewConfig } from '../components/data-list-view';
import type { DataSource, LiveDataSource } from '../components/data-source';
import type { TablePaneConfig, ListPaneConfig, GridPaneConfig, ListPaneInstanceData } from './types';

/**
 * Creates a table-only pane type.
 *
 * Table panes display data in a traditional table layout with:
 * - Column headers with sorting
 * - Row selection (optional)
 * - Heat map shading (optional)
 * - Sticky header (optional)
 *
 * @example
 * ```typescript
 * const userTablePane = createTablePaneType<User>({
 *     typeId: 'user-table',
 *     defaultTitle: 'Users',
 *     createDataSource: (ctx) => new DataSource({
 *         fetch: (params) => fetchUsers(params),
 *         keyFn: (u) => u.id,
 *         pageSize: 64
 *     }),
 *     columns: userColumns,
 *     keyFn: (u) => u.id,
 *     selectionMode: 'multi',
 *     onItemActivate: (user, ctx) => {
 *         ctx.shell.openPane({ typeId: 'user-detail', context: { userId: user.id } });
 *     },
 *     ariaLabel: 'User list'
 * });
 *
 * shell.registerPaneType(userTablePane);
 * ```
 */
export function createTablePaneType<T>(config: TablePaneConfig<T>): PaneType {
    // Track instances for cleanup
    const instances = new Map<string, ListPaneInstanceData<T>>();

    // Derive capabilities from config
    const capabilities = deriveCapabilities(config);

    // Per-instance (ctx, dataSource) for the title-row slot, which the surface
    // calls with only the instance. Set in render, cleared in onDestroy.
    const renderState = new WeakMap<PaneInstance, { ctx: PaneTypeRenderContext; dataSource: DataSource<T> | LiveDataSource<T> }>();

    return {
        typeId: config.typeId,
        kind: 'leaf',
        tabBarActions: config.tabBarActions
            ? (instance) => {
                const st = renderState.get(instance);
                return st ? config.tabBarActions!(st.ctx, st.dataSource) : [];
            }
            : undefined,
        defaultTitle: config.defaultTitle,
        capabilities,
        defaultFlags: config.defaultFlags,

        render: (ctx: PaneTypeRenderContext): HTMLElement => {
            // Create data source for this instance
            const dataSource = config.createDataSource(ctx);
            renderState.set(ctx.instance, { ctx, dataSource });

            // Build layout slots
            const header = config.createHeader?.(dataSource, ctx);
            const filterBar = config.createFilterBar?.(dataSource, ctx);
            const toolbar = config.createToolbar?.(dataSource, ctx);

            const { element: container, body } = createListPaneContainer({
                header, filterBar, toolbar,
                className: 'cs-table-pane',
            });

            // Build DataListView config
            const listViewConfig: DataListViewConfig<T> = {
                container: body,
                dataSource,
                columns: config.columns,
                keyFn: config.keyFn,
                ariaLabel: resolveAriaLabel(config.ariaLabel, ctx.instance),

                // Force table-only mode
                initialMode: 'table',
                enabledModes: ['table'],

                // Table-specific
                tableRowHeight: config.tableRowHeight,
                stickyHeader: config.stickyHeader,

                // Selection
                selectionMode: config.selectionMode,
                bulkActions: config.bulkActions,
                canSelect: config.canSelect,
                selectAllBehavior: config.selectAllBehavior,

                // Features
                enableHeatMap: config.enableHeatMap,
                enableRowStates: config.enableRowStates,

                // Persistence
                persistenceKey: derivePersistenceKey(config.persistenceKeyPrefix, ctx.instance),

                // Events - wire up with context
                onItemSelect: config.onItemSelect
                    ? (item) => {
                        // Persist active key for restoration after page reload
                        // and so the pane-closed listener below can clear it
                        // when the detail pane closes.
                        ctx.instance.context.__activeKey = config.keyFn(item);
                        config.onItemSelect!(item, ctx);
                    }
                    : undefined,
                onItemActivate: config.onItemActivate
                    ? (item) => config.onItemActivate!(item, ctx)
                    : undefined,
                onSelectionChange: config.onSelectionChange
                    ? (state) => config.onSelectionChange!(state, ctx)
                    : undefined,
                onSortChange: config.onSortChange
                    ? (col, dir) => config.onSortChange!(col, dir, ctx)
                    : undefined,
            };

            // Create DataListView
            const dataListView = new DataListView(listViewConfig);

            // Auto-start LiveDataSource if applicable
            if ('startListening' in dataSource && typeof (dataSource as LiveDataSource<T>).startListening === 'function') {
                (dataSource as LiveDataSource<T>).startListening();
            }

            // Store instance data for cleanup and access
            instances.set(ctx.instance.instanceId, { dataListView, dataSource });

            // For single-selection table panes that open detail panes:
            // clear the row highlight when the detail pane closes (i.e.
            // when this pane becomes the last one in the surface).
            // Deferred via queueMicrotask because openPane removes the old
            // detail (emitting pane-closed) before adding the new one —
            // without deferral we'd see the table as the last pane between
            // the two events and incorrectly clear the selection.
            // Mirrors the same logic in list-pane.ts; without it, the
            // visually-selected row stays highlighted forever after the
            // user closes the detail pane.
            if (config.onItemSelect && ctx.surface) {
                const surface = ctx.surface;
                const myInstanceId = ctx.instance.instanceId;
                ctx.disposables.add(ctx.shell.on('pane-closed', (data) => {
                    const { surfaceId } = data as { surfaceId: string };
                    if (surfaceId !== surface.surfaceId) return;
                    queueMicrotask(() => {
                        const panes = surface.getPanes();
                        const myIndex = panes.findIndex(p => p.instanceId === myInstanceId);
                        if (myIndex >= 0 && myIndex === panes.length - 1) {
                            dataListView.clearActiveKey();
                            delete ctx.instance.context.__activeKey;
                        }
                    });
                }));
            }

            // Restore active key from persisted context after data loads.
            // Same shape as list-pane.ts: when a pane is rebuilt (e.g. page
            // reload restoring the saved layout) and a previous selection
            // exists in context, re-highlight the matching row once the
            // data source delivers the item.
            const restoredKey = ctx.instance.context.__activeKey as string | undefined;
            if (restoredKey && config.onItemSelect) {
                const restoreCallback = config.onItemRestore;
                const tryRestore = () => {
                    const item = dataSource.getItemByKey(restoredKey);
                    if (item) {
                        dataListView.setActiveKey(restoredKey);
                        restoreCallback?.(item, ctx);
                        unsub();
                    }
                };
                const unsub = dataSource.on('change', tryRestore);
                ctx.disposables.add(unsub);
            }

            // Invoke onCreated hook
            config.onCreated?.({ dataListView, dataSource, container }, ctx);

            // The header was built before this render, so the title row is
            // still empty: ask for it now.
            refreshTabBarActionsAfterRender(config, ctx);

            return container;
        },

        onActivate: (instance: PaneInstance) => {
            const data = instances.get(instance.instanceId);
            if (data) {
                // Focus the list when pane is activated
                data.dataListView.focus();
            }
        },

        onDestroy: (instance: PaneInstance) => {
            renderState.delete(instance);
            const data = instances.get(instance.instanceId);
            if (data) {
                data.dataListView.dispose();

                if ('dispose' in data.dataSource && typeof data.dataSource.dispose === 'function') {
                    data.dataSource.dispose();
                }

                instances.delete(instance.instanceId);
            }
        },
    };
}

// ============================================================================
// Helper Functions (exported for reuse by other factories)
// ============================================================================

/**
 * Fill the title row after the first paint.
 *
 * Both surfaces build a pane's header — and call `tabBarActions` — BEFORE they
 * render its body, and a factory's slot has nothing to hand back until
 * `render` has stored the (ctx, dataSource) it closes over. Left alone, a
 * factory pane on a flow surface therefore opens with an EMPTY row and stays
 * that way until something else happens to refresh it. So the factories ask
 * for one refresh at the end of `render`, and a pane that declares no slot
 * asks for nothing.
 *
 * Guarded because a bare test harness may hand in a stub shell.
 */
export function refreshTabBarActionsAfterRender<T>(
    config: TablePaneConfig<T> | GridPaneConfig<T> | ListPaneConfig<T>,
    ctx: PaneTypeRenderContext,
): void {
    if (!config.tabBarActions) return;
    ctx.shell?.refreshPaneActions?.(ctx.instance.instanceId);
}

/**
 * Derive capabilities from pane configuration.
 */
export function deriveCapabilities<T>(
    config: TablePaneConfig<T> | GridPaneConfig<T> | ListPaneConfig<T>
): string[] {
    const capabilities: string[] = [];

    // Selection capability
    if (config.selectionMode === 'single' || config.selectionMode === 'multi') {
        capabilities.push('selection');
    }

    // Bulk actions capability
    if (config.bulkActions && config.bulkActions.length > 0) {
        capabilities.push('bulk-actions');
    }

    // Add user-specified capabilities
    if (config.capabilities) {
        for (const cap of config.capabilities) {
            if (!capabilities.includes(cap)) {
                capabilities.push(cap);
            }
        }
    }

    return capabilities;
}

/**
 * Resolve aria-label from config (static or dynamic).
 */
export function resolveAriaLabel(
    ariaLabel: string | ((instance: PaneInstance) => string),
    instance: PaneInstance
): string {
    return typeof ariaLabel === 'function' ? ariaLabel(instance) : ariaLabel;
}

/**
 * Derive persistence key from prefix and instance context.
 * Returns undefined if no prefix is configured.
 */
export function derivePersistenceKey(
    prefix: string | undefined,
    instance: PaneInstance
): string | undefined {
    if (!prefix) return undefined;

    // Include instance context in key for instance-specific persistence
    const contextHash = hashContext(instance.context);
    return `${prefix}_${instance.typeId}_${contextHash}`;
}

/**
 * Simple hash of instance context for persistence key.
 */
function hashContext(context: Record<string, unknown>): string {
    // Use sorted JSON for deterministic hashing
    const sorted = Object.keys(context)
        .filter(k => !k.startsWith('__')) // Exclude internal keys
        .sort()
        .map(k => `${k}:${JSON.stringify(context[k])}`)
        .join('|');

    // Simple hash (not cryptographic, just for key uniqueness)
    let hash = 0;
    for (let i = 0; i < sorted.length; i++) {
        const char = sorted.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(36);
}
